// trash icon from hereo icons
import { TrashIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'

import { yupResolver } from '@hookform/resolvers/yup'
import { DynamicFormProps } from '@ui/interfaces'
import { isValidUrl } from '@ui/lib/helpers/validationHelpers'
// TODO: remove classNames dependency use clsx instead
import classNames from '@ui/lib/utils/classNames'
import { SaveAndCancelButtons } from '@ui/pages/pipelinesWorkflow/components/DestinationForms/SaveAndCancelButtons'
import * as Yup from 'yup'

export default function HttpJsonPost(props: DynamicFormProps) {
  const schema = Yup.object({
    'url-logs': Yup.string().test('is-url-valid', 'URL is not valid', isValidUrl).required('URL must be provided'),
    'url-traces': Yup.string().test('is-url-valid', 'URL is not valid', isValidUrl).required('URL must be provided'),
    headers: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required('Header name is required'),
        value: Yup.string().required('Header value is required'),
      })
    ),
  }).required()

  const [useLogsUrlForTraces, setUseLogsUrlForTraces] = useState(false)

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      ...props.config,
    },
  })

  const logsUrl = watch('url-logs')
  useEffect(() => {
    if (useLogsUrlForTraces) {
      setValue('url-traces', logsUrl)
    }
  }, [useLogsUrlForTraces, logsUrl, setValue])

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'headers',
  })

  const { saveClick, cancelClick } = props

  return (
    <form onSubmit={handleSubmit(saveClick)}>
      <div className="grid grid-cols-1 gap-x-6 gap-y-8">
        <fieldset className="sm:col-span-4">
          {props.additionalInputs}
          <div className="flex w-full justify-between">
            <label htmlFor="url-logs" className="block text-sm font-medium leading-6 text-gray-900">
              Logs URL*
            </label>

            <div className="flex items-center">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200/50"
                checked={useLogsUrlForTraces}
                onChange={(e) => setUseLogsUrlForTraces(e.target.checked)}
                name="useForTraces"
                id="useForTraces"
                disabled={props.disableInputs}
              />
              <label htmlFor="useForTraces" className="ml-2 block text-sm font-medium text-gray-900">
                Use for traces too
              </label>
            </div>
          </div>
          <div className="mt-2 w-full">
            <input
              {...register('url-logs')}
              type="text"
              name="url-logs"
              id="url-logs"
              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              placeholder="https://example.com"
              disabled={props.disableInputs}
            />
            {errors['url-logs'] && <p className="mt-2 text-xs italic text-red-500">{errors['url-logs'].message}</p>}
          </div>
          <label htmlFor="url-traces" className="mt-2 block text-sm font-medium leading-6 text-gray-900">
            Traces URL*
          </label>
          <div className="mt-2">
            <div className="flex w-full">
              <input
                {...register('url-traces')}
                id="url-traces"
                name="url-traces"
                type="text"
                className={classNames(
                  useLogsUrlForTraces ? 'bg-gray-200 text-gray-400' : 'text-gray-900',
                  'block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6'
                )}
                placeholder="https://example.com"
                disabled={props.disableInputs || useLogsUrlForTraces}
              />
            </div>
            {errors['url-traces'] && <p className="mt-2 text-xs italic text-red-500">{errors['url-traces'].message}</p>}
          </div>
        </fieldset>
      </div>
      <div className="mb-12 mt-6">
        <fieldset>
          <div className="flex items-center gap-x-4">
            <legend className="text-sm font-semibold leading-6 text-gray-900">Headers</legend>
            <button
              type="button"
              className="self-center rounded-md bg-primary-600 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              onClick={() => {
                if (props.disableInputs) return
                append({ name: '', value: '' })
              }}>
              + Add
            </button>
          </div>
          {fields.map((field, index) => (
            <div key={field.id} className="mt-4">
              <div className="grid grid-cols-11 gap-4 text-sm">
                <input
                  {...register(`headers.${index}.name` as const)}
                  className="col-span-5 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  placeholder="Header Name"
                  disabled={props.disableInputs}
                />
                <input
                  {...register(`headers.${index}.value` as const)}
                  className="col-span-5 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  placeholder="Header Value"
                  disabled={props.disableInputs}
                />
                <button
                  type="button"
                  className="justify-self-center rounded-md bg-red-600 p-3 text-xs font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  onClick={() => {
                    if (props.disableInputs) return
                    remove(index)
                  }}>
                  <TrashIcon className="h-4 w-4" />
                </button>
                <div className="col-span-5">
                  {errors?.headers?.[index]?.name && (
                    <p className="text-xs italic text-red-500">{errors?.headers?.[index]?.name?.message}</p>
                  )}
                </div>
                <div className="col-span-5">
                  {errors?.headers?.[index]?.value && (
                    <p className="text-xs italic text-red-500">{errors?.headers?.[index]?.value?.message}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </fieldset>
      </div>
      {props.submitButton ? props.submitButton : <SaveAndCancelButtons cancelClick={cancelClick} />}
    </form>
  )
}
